// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { enqueueSnackbarMessage } from "../commonSlice/common";
import { SnackMessage } from "../../config/constant";
import axios, { HttpStatusCode } from "axios";
import { State } from "../../utils/types";

export interface DefaultTeam {
  teamId: number;
  teamName: string;
  permissionLevel: string;
}

interface DefaultTeamsState {
  state: State;
  functionType?: string;
  defaultTeams: DefaultTeam[] | undefined;
  errorMessage: string | null;
}

const initialState: DefaultTeamsState = {
  state: State.idle,
  defaultTeams: undefined,
  errorMessage: null,
};

export interface AddDefaultTeamPayload {
  teamName: string;
  permissionLevel: string;
}

export const fetchDefaultTeams = createAsyncThunk(
  "defaultTeams/fetchDefaultTeams",
  async (_, { dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    const newCancelTokenSource = APIService.updateCancelToken();
    try {
      const response = await APIService.getInstance().get(AppConfig.serviceUrls.defaultTeams, {
        cancelToken: newCancelTokenSource.token,
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({canceled : true});
      }
      if (axios.isAxiosError(error)) {
        dispatch(
          enqueueSnackbarMessage({
            message:
              error.response?.status === HttpStatusCode.InternalServerError
                ? SnackMessage.error.fetchDefaultTeamsMessage
                : String(error.response?.data?.message || "Unknown error"),
            type: "error",
          })
        );
        return rejectWithValue(error.response?.data || "Failed to fetch default teams");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const addDefaultTeam = createAsyncThunk(
  "defaultTeams/addDefaultTeam",
  async (payload: AddDefaultTeamPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await APIService.getInstance().post(
        AppConfig.serviceUrls.defaultTeams,
        payload
      );
      dispatch(
        enqueueSnackbarMessage({
          message: SnackMessage.success.addDefaultTeamMessage,
          type: "success",
        })
      );
      return response.data;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.status === HttpStatusCode.InternalServerError
          ? SnackMessage.error.addDefaultTeamMessage
          : String(error.response?.data?.message || error.message || "Unknown error")
        : "An unexpected error occurred";
      dispatch(enqueueSnackbarMessage({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

export const updateDefaultTeam = createAsyncThunk(
  "defaultTeams/updateTeam",
  async (payload: DefaultTeam, { dispatch, rejectWithValue }) => {
    try {
      const response = await APIService.getInstance().put(
        `${AppConfig.serviceUrls.defaultTeams}/${payload.teamId}`,
        payload
      );
      dispatch(
        enqueueSnackbarMessage({
          message: SnackMessage.success.updateDefaultTeamMessage,
          type: "success",
        })
      );
      return response.data;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.status === HttpStatusCode.InternalServerError
          ? SnackMessage.error.updateDefaultTeamMessage
          : String(error.response?.data?.message || error.message || "Unknown error")
        : "An unexpected error occurred";
      dispatch(enqueueSnackbarMessage({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

export const deleteDefaultTeam = createAsyncThunk(
  "defaultTeams/deleteDefaultTeam",
  async (teamId: number, { dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    try {
      const response = await APIService.getInstance().delete(
        `${AppConfig.serviceUrls.defaultTeams}/${teamId}`
      );
      dispatch(
        enqueueSnackbarMessage({
          message: SnackMessage.success.deleteDefaultTeamMessage,
          type: "success",
        })
      );
      return teamId;
    } catch (error) {
      let errorMessage = "Failed to delete default team";
      if (axios.isAxiosError(error)) {
        errorMessage =
          error.response?.status === HttpStatusCode.InternalServerError
            ? SnackMessage.error.deleteDefaultTeamMessage
            : String(error.response?.data?.message || errorMessage);
      }
      dispatch(
        enqueueSnackbarMessage({
          message: errorMessage,
          type: "error",
        })
      );
      return rejectWithValue(
        errorMessage
      );
    }
  }
);

const defaultTeamsSlice = createSlice({
  name: "defaultTeams",
  initialState,
  reducers: {
    resetDefaultTeamsState(state) {
      state.state = State.idle;
      state.defaultTeams = undefined;
      state.errorMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDefaultTeams.pending, (state) => {
        state.state = State.loading;
        state.functionType = "fetch";
        state.errorMessage = "Loading default teams...";
      })
      .addCase(fetchDefaultTeams.fulfilled, (state, action) => {
        state.state = State.success;
        state.functionType = undefined;
        state.errorMessage = null;
        state.defaultTeams = action.payload;
      })
      .addCase(fetchDefaultTeams.rejected, (state, action) => {
        if ((action.payload as any)?.canceled) {
          if (!state.defaultTeams) {
            state.state = State.idle;
            state.errorMessage = null;
          }
          return;
        }
        state.state = State.failed;
        state.functionType = undefined;
        state.errorMessage = "Failed to load default teams";
      })
      .addCase(addDefaultTeam.pending, (state) => {
        state.state = State.loading;
        state.functionType = "create";
        state.errorMessage = "Adding default team...";
      })
      .addCase(addDefaultTeam.fulfilled, (state) => {
        state.state = State.success;
        state.functionType = undefined;
        state.errorMessage = null;
      })
      .addCase(addDefaultTeam.rejected, (state, action) => {
        state.state = State.failed;
        state.functionType = undefined;
        state.errorMessage = "Failed to add default team";
      })
      .addCase(updateDefaultTeam.pending, (state) => {
        state.state = State.loading;
        state.functionType = "update";
        state.errorMessage = "Updating default team...";
      })
      .addCase(updateDefaultTeam.fulfilled, (state) => {
        state.state = State.success;
        state.functionType = undefined;
        state.errorMessage = null;
      })
      .addCase(updateDefaultTeam.rejected, (state, action) => {
        state.state = State.failed;
        state.functionType = undefined;
        state.errorMessage = "Failed to update default team";
      })
      .addCase(deleteDefaultTeam.pending, (state) => {
        state.state = State.loading;
        state.functionType = "delete";
        state.errorMessage = "Deleting default team...";
      })
      .addCase(deleteDefaultTeam.fulfilled, (state, action) => {
        state.state = State.success;
        state.functionType = undefined;
        state.errorMessage = null;
      })
      .addCase(deleteDefaultTeam.rejected, (state, action) => {
        state.state = State.failed;
        state.functionType = undefined;
        state.errorMessage = "Failed to delete default team";
      });
  },
});

export const { resetDefaultTeamsState } = defaultTeamsSlice.actions;
export default defaultTeamsSlice.reducer;